import { useState, useEffect } from 'react'
import { isHTTPError } from 'ky'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, UserPlus } from 'lucide-react'
import type { CreateUserResponse, AssignableRole } from '@common/types'
import { ROLE_LABELS } from '@common/permissions'
import { createUser } from '@frontend/lib/api'
import { usePermissions } from '@frontend/hooks/usePermissions'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'

interface AddUserModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddUserModal({ open, onOpenChange }: AddUserModalProps) {
  const queryClient = useQueryClient()
  const { getAssignableRoles } = usePermissions()

  // Form state
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<AssignableRole>('basic_user')
  const [error, setError] = useState<string | null>(null)

  // Get roles the current user can assign
  const assignableRoles = getAssignableRoles()

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setEmail('')
      setRole('basic_user')
      setError(null)
    }
  }, [open])

  const createMutation = useMutation({
    mutationFn: () => createUser({ email: email.trim(), role }),
    onSuccess: (data: CreateUserResponse) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      onOpenChange(false)
      toast.success(`User "${data.user.email}" created`)
    },
    onError: (err) => {
      if (isHTTPError(err)) {
        const data = err.data as { error?: string }
        setError(data.error || 'Failed to create user')
      } else {
        setError('Failed to create user')
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      setError('Email is required')
      return
    }
    if (!trimmedEmail.endsWith('@edendata.com')) {
      setError('Email must be an @edendata.com address')
      return
    }

    createMutation.mutate()
  }

  const isSubmitting = createMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add User
          </DialogTitle>
          <DialogDescription>
            Pre-create a user account so they have access immediately when they first log in.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="name@edendata.com"
              disabled={isSubmitting}
              autoComplete="off"
              data-1p-ignore
            />
          </div>

          {/* Role */}
          <div>
            <label htmlFor="role" className="block text-sm font-medium mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as AssignableRole)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              disabled={isSubmitting}
            >
              {assignableRoles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              {role === 'basic_user' && 'View reviews and manage clients'}
              {role === 'manager' && 'Edit compliance reviews'}
              {role === 'admin' && 'Approve/archive reviews, manage users'}
            </p>
          </div>

          {/* Error display */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Creating...
                </>
              ) : (
                'Add User'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
